import { Component } from '@theme/component';

/**
 * @typedef {Object} CourseRefs
 * @property {HTMLElement[]} [reveal]
 * @property {HTMLElement[]} [chapters]
 * @property {HTMLAnchorElement[]} [chapterLinks]
 * @property {HTMLElement} [currentNumber]
 */

/** Natural document scrolling; enhancement never gates access to the lessons.
 * @extends {Component<CourseRefs>}
 */
class SeedsCourse extends Component {
  /** @type {AbortController | undefined} */
  #controller;
  /** @type {IntersectionObserver | undefined} */
  #revealObserver;
  /** @type {IntersectionObserver | undefined} */
  #chapterObserver;
  /** @type {MediaQueryList | undefined} */
  #motion;
  /** @type {number | undefined} */
  #openingTimer;
  /** @type {number | undefined} */
  #openingFrame;

  connectedCallback() { super.connectedCallback(); this.#setup(); }
  updatedCallback() { super.updatedCallback(); this.#setup(); }
  disconnectedCallback() { this.#cleanup(); super.disconnectedCallback(); }

  #cleanup() {
    this.#controller?.abort();
    this.#revealObserver?.disconnect();
    this.#chapterObserver?.disconnect();
    clearTimeout(this.#openingTimer);
    if (this.#openingFrame !== undefined) cancelAnimationFrame(this.#openingFrame);
    delete this.dataset.openingPending;
  }

  #setup() {
    this.#cleanup();
    this.#controller = new AbortController();
    this.#motion = matchMedia('(prefers-reduced-motion: reduce)');
    this.#motion.addEventListener('change', this.#configureReveals, { signal: this.#controller.signal });
    this.#configureReveals();
    this.#startOpening();
    const chapters = this.refs.chapters ?? [];
    if (!chapters.length || !('IntersectionObserver' in window)) return;
    this.#chapterObserver = new IntersectionObserver(this.#updateChapter, {
      rootMargin: '-15% 0px -35% 0px', threshold: [0, .15, .4, .65, 1],
    });
    for (const chapter of chapters) this.#chapterObserver.observe(chapter);
    this.#updateChapter();
  }

  #startOpening() {
    if (!this.hasAttribute('data-opening') || this.hasAttribute('data-opening-started') || this.dataset.motion !== 'true' || this.#motion?.matches) return;
    const heading = this.querySelector('h1');
    // A delayed module must not hide and replay an already completed CSS entrance.
    if (heading?.getAnimations().some((animation) => animation.playState === 'finished')) {
      this.dataset.openingStarted = '';
      return;
    }
    this.dataset.openingPending = '';
    const signal = this.#controller?.signal;
    let scheduled = false;
    const start = () => {
      if (signal?.aborted || scheduled) return;
      scheduled = true;
      clearTimeout(this.#openingTimer);
      // Two painted frames separate the waiting state from the entrance.
      this.#openingFrame = requestAnimationFrame(() => {
        this.#openingFrame = requestAnimationFrame(() => {
          if (signal?.aborted) return;
          delete this.dataset.openingPending;
          this.dataset.openingStarted = '';
        });
      });
    };
    // A slow or unavailable webfont must never leave the opening hidden.
    this.#openingTimer = window.setTimeout(start, 1800);
    document.fonts.ready.then(start, start);
  }

  #configureReveals = () => {
    this.#revealObserver?.disconnect();
    const items = [...(this.refs.reveal ?? []), ...(this.refs.chapters ?? [])];
    if (this.dataset.motion !== 'true' || this.#motion?.matches || !('IntersectionObserver' in window)) {
      for (const item of items) item.setAttribute('data-revealed', '');
      return;
    }
    this.dataset.enhanced = 'true';
    this.#revealObserver = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        entry.target.setAttribute('data-revealed', '');
        this.#revealObserver?.unobserve(entry.target);
      }
    }, { threshold: .2, rootMargin: '0px 0px -12% 0px' });
    for (const item of items) {
      if (!item.hasAttribute('data-revealed')) this.#revealObserver.observe(item);
    }
  };

  #updateChapter = () => {
    const chapters = this.refs.chapters ?? [];
    const links = this.refs.chapterLinks ?? [];
    if (!chapters.length) return;
    const point = window.innerHeight * .45;
    let activeIndex = 0;
    let nearest = Infinity;
    for (const [index, chapter] of chapters.entries()) {
      const bounds = chapter.getBoundingClientRect();
      const distance = Math.abs(bounds.top + bounds.height / 2 - point);
      if (distance < nearest) { nearest = distance; activeIndex = index; }
    }
    for (const [index, chapter] of chapters.entries()) {
      chapter.dataset.active = String(index === activeIndex);
      if (this.dataset.activeColor) chapter.classList.toggle(this.dataset.activeColor, index === activeIndex);
    }
    for (const [index, link] of links.entries()) {
      if (index === activeIndex) link.setAttribute('aria-current', 'step');
      else link.removeAttribute('aria-current');
      if (this.dataset.activeColor) link.classList.toggle(this.dataset.activeColor, index === activeIndex);
    }
    if (this.refs.currentNumber) this.refs.currentNumber.textContent = String(activeIndex + 1).padStart(2, '0');
  };
}

if (!customElements.get('seeds-course-component')) customElements.define('seeds-course-component', SeedsCourse);
